import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, Users, UserCog, Plus, Check, X, Trash2, Cake, Edit2 } from 'lucide-react';
import employeeApi from '../../lib/employeeApi';
import EmployeeLayout from '../../components/EmployeeLayout';

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [editingDept, setEditingDept] = useState(null); // dept name currently being edited
  const [savingDept, setSavingDept] = useState(null);
  const [addForm, setAddForm] = useState({ open: false, department: '', employee_id: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [detailRes, teamRes, deptRes] = await Promise.all([
        employeeApi.get(`/api/employee/clients/${clientId}`),
        employeeApi.get('/api/employee/team'),
        employeeApi.get('/api/employee/departments'),
      ]);
      setData(detailRes.data);
      setTeam(teamRes.data || []);
      const depts = Array.isArray(deptRes.data) ? deptRes.data : (deptRes.data?.departments || []);
      setAllDepts(depts.map(d => d.name || d));
    } catch (error) {
      toast.error('Failed to load client');
    }
  }, [clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const savePOC = async (department, employee_id) => {
    setSavingDept(department);
    try {
      await employeeApi.put(`/api/employee/clients/${clientId}/poc`, { department, employee_id: employee_id || null });
      toast.success(employee_id ? `POC set for ${department}` : `POC removed for ${department}`);
      setEditingDept(null);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save POC');
    } finally { setSavingDept(null); }
  };

  const addDepartment = async () => {
    if (!addForm.department) { toast.error('Pick a department'); return; }
    try {
      await employeeApi.post(`/api/employee/clients/${clientId}/departments`, {
        department: addForm.department,
        employee_id: addForm.employee_id || null,
      });
      toast.success(`Department "${addForm.department}" added`);
      setAddForm({ open: false, department: '', employee_id: '' });
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add department');
    }
  };

  if (!data) return <EmployeeLayout><div className="p-8 text-center">Loading...</div></EmployeeLayout>;

  const clientDepts = data.client.departments || [];
  const pocByDept = Object.fromEntries((data.pocs || []).map(p => [p.department, p]));
  // Depts the client has but no POC yet — surfaced separately so nothing is hidden.
  const deptsWithoutPoc = clientDepts.filter(d => !pocByDept[d]);
  const availableDepts = allDepts.filter(d => !clientDepts.includes(d));

  return (
    <EmployeeLayout>
      <div className="p-8 max-w-6xl mx-auto">
        <Link to="/employee/clients" className="inline-flex items-center text-neutral-600 hover:text-pink-600 mb-6" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Link>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2" data-testid="client-name">{data.client.name}</h1>
          <p className="text-neutral-600">{data.client.industry || data.client.category}</p>
          <BrandAnniversaryEditor
            clientId={clientId}
            initial={data.client.brand_anniversary_date || ''}
            onSaved={(v) => setData(d => ({ ...d, client: { ...d.client, brand_anniversary_date: v } }))}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <StatCard icon={Briefcase} label="Total Tasks" value={data.task_count} />
          <StatCard icon={Users} label="Departments" value={clientDepts.length} />
          <StatCard icon={UserCog} label="POCs Assigned" value={(data.pocs || []).length} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* POC Manager — the whole point of this page for employees. */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="poc-manager">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">Points of Contact</h3>
              <button
                onClick={() => setAddForm(f => ({ ...f, open: !f.open }))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-pink-600 text-white hover:bg-pink-700"
                data-testid="add-dept-toggle">
                <Plus className="w-3.5 h-3.5" /> Add Department
              </button>
            </div>

            {/* Add Department form */}
            {addForm.open && (
              <div className="mb-4 p-3 border border-pink-200 bg-pink-50/60 rounded-lg space-y-2" data-testid="add-dept-form">
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={addForm.department}
                    onChange={(e) => setAddForm(f => ({ ...f, department: e.target.value }))}
                    className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="add-dept-select">
                    <option value="">— Department —</option>
                    {availableDepts.length === 0 ? (
                      <option disabled>All departments already added</option>
                    ) : availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value={addForm.employee_id}
                    onChange={(e) => setAddForm(f => ({ ...f, employee_id: e.target.value }))}
                    className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="add-dept-poc-select">
                    <option value="">— POC (optional) —</option>
                    {team.map(e => (
                      <option key={e._id} value={e._id}>{e.personal?.full_name || e.work_email}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setAddForm({ open: false, department: '', employee_id: '' })}
                    className="px-3 py-1 text-xs text-neutral-600 hover:bg-neutral-100 rounded-lg" data-testid="add-dept-cancel">Cancel</button>
                  <button onClick={addDepartment}
                    className="px-3 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="add-dept-save">Add</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {/* Rows for depts WITH a POC */}
              {(data.pocs || []).map(poc => (
                <POCRow
                  key={poc.department}
                  department={poc.department}
                  poc={poc}
                  team={team}
                  editing={editingDept === poc.department}
                  saving={savingDept === poc.department}
                  onEdit={() => setEditingDept(poc.department)}
                  onCancel={() => setEditingDept(null)}
                  onSave={(empId) => savePOC(poc.department, empId)}
                  onUnset={() => savePOC(poc.department, null)}
                />
              ))}
              {/* Rows for client depts WITHOUT a POC yet */}
              {deptsWithoutPoc.map(d => (
                <POCRow
                  key={d}
                  department={d}
                  poc={null}
                  team={team}
                  editing={editingDept === d}
                  saving={savingDept === d}
                  onEdit={() => setEditingDept(d)}
                  onCancel={() => setEditingDept(null)}
                  onSave={(empId) => savePOC(d, empId)}
                />
              ))}
              {(data.pocs || []).length === 0 && deptsWithoutPoc.length === 0 && (
                <p className="text-sm text-neutral-500 italic">No departments yet — click <strong>Add Department</strong> to get started.</p>
              )}
            </div>
          </div>

          {/* Task summary */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Recent Tasks</h3>
            <div className="space-y-2">
              {data.tasks.slice(0, 10).map(task => (
                <div key={task._id} className="p-3 bg-neutral-50 rounded-lg">
                  <p className="font-medium text-neutral-900 text-sm">{task.title}</p>
                  <p className="text-xs text-neutral-600">{task.department} · {task.status}</p>
                </div>
              ))}
              {data.tasks.length === 0 && <p className="text-sm text-neutral-500 italic">No tasks yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </EmployeeLayout>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-6">
      <Icon className="w-7 h-7 text-pink-600 mb-3" />
      <p className="text-sm text-neutral-600">{label}</p>
      <p className="text-3xl font-bold text-neutral-900">{value}</p>
    </div>
  );
}

function POCRow({ department, poc, team, editing, saving, onEdit, onCancel, onSave, onUnset }) {
  const [draft, setDraft] = useState(poc?.employee_id_raw || '');
  useEffect(() => { setDraft(poc?.employee_id_raw || ''); }, [poc, editing]);

  return (
    <div className="p-3 bg-neutral-50 rounded-lg" data-testid={`poc-row-${department}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-neutral-900 text-sm">{department}</p>
          {!editing ? (
            poc ? (
              <p className="text-xs text-neutral-600 mt-0.5">
                {poc.employee_name} <span className="text-neutral-400">· {poc.employee_id}</span>
              </p>
            ) : (
              <p className="text-xs text-amber-600 italic mt-0.5">No POC assigned — click Set POC</p>
            )
          ) : (
            <select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="mt-2 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
              disabled={saving}
              data-testid={`poc-select-${department}`}>
              <option value="">— No POC —</option>
              {team.map(e => (
                <option key={e._id} value={e._id}>
                  {e.personal?.full_name || e.work_email}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {!editing ? (
            <>
              <button onClick={onEdit} disabled={saving}
                className="px-2 py-1 text-xs border border-neutral-300 rounded-lg hover:bg-white"
                data-testid={`poc-edit-${department}`}>
                {poc ? 'Change' : 'Set POC'}
              </button>
              {poc && onUnset && (
                <button onClick={onUnset} disabled={saving}
                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Unset POC" data-testid={`poc-unset-${department}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={() => onSave(draft)} disabled={saving}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-50"
                data-testid={`poc-save-${department}`}>
                <Check className="w-4 h-4" />
              </button>
              <button onClick={onCancel} disabled={saving}
                className="p-1 text-neutral-500 hover:bg-neutral-100 rounded-lg"
                data-testid={`poc-cancel-${department}`}>
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Brand Anniversary inline editor ─────────────────────────────────────
// Any employee viewing a client can fill in / update the brand's founding
// date. Feeds the 60-day-advance campaign heads-up notification. Single
// PUT — never exposes any other client field.
function BrandAnniversaryEditor({ clientId, initial, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(initial || ''); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await employeeApi.put(`/api/employee/clients/${clientId}/brand-anniversary`, {
        brand_anniversary_date: draft || null,
      });
      toast.success(draft ? 'Brand anniversary saved' : 'Anniversary cleared');
      onSaved?.(r.data.brand_anniversary_date || '');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Human display: "12 Aug 2018 (7 years ago)". Falls back to raw string
  // for MM-DD (year-less) values.
  const displayDate = (() => {
    if (!initial) return null;
    if (initial.length === 10) {
      const d = new Date(initial);
      if (isNaN(d)) return initial;
      const years = new Date().getFullYear() - d.getFullYear();
      return `${d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}${years > 0 ? ` · ${years} year${years === 1 ? '' : 's'} ago` : ''}`;
    }
    return initial;
  })();

  if (!editing) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-pink-50 border border-pink-100 rounded-full text-sm" data-testid="brand-anniversary-display">
        <Cake className="w-4 h-4 text-pink-600" />
        <span className="text-neutral-700">
          {displayDate ? <><strong>Brand Anniversary:</strong> {displayDate}</> : <em className="text-neutral-500">No brand anniversary set — help account managers plan campaigns 60 days ahead.</em>}
        </span>
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 text-pink-700 hover:text-pink-900 text-xs font-medium"
          data-testid="brand-anniversary-edit-btn">
          <Edit2 className="w-3 h-3" />{displayDate ? 'Change' : 'Add'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-2" data-testid="brand-anniversary-editor">
      <input
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm"
        disabled={saving}
        data-testid="brand-anniversary-input"
      />
      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-600 text-white rounded-lg text-sm hover:bg-pink-700 disabled:opacity-50"
        data-testid="brand-anniversary-save">
        <Check className="w-4 h-4" />Save
      </button>
      <button
        onClick={() => { setDraft(initial || ''); setEditing(false); }}
        disabled={saving}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-sm hover:bg-neutral-200"
        data-testid="brand-anniversary-cancel">
        <X className="w-4 h-4" />Cancel
      </button>
      {initial && (
        <button
          onClick={() => { setDraft(''); }}
          disabled={saving}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-neutral-500 hover:text-red-600 text-xs"
          data-testid="brand-anniversary-clear">
          <Trash2 className="w-3.5 h-3.5" />Clear
        </button>
      )}
    </div>
  );
}
